import type { TypedDataSigner } from '@ethersproject/abstract-signer'
import { FC, Ref, useEffect, useState, createRef } from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import { useAddress, useChainId, useSetIsWalletModalOpen, useSigner } from '@thirdweb-dev/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRotateRight } from '@fortawesome/pro-regular-svg-icons'
import { BigNumber, Signer } from 'ethers'
import requestMint from '~/modules/Trophies/RequestMint/Application/RequestMint'
import { SafePlayVideo, SafeStopVideo } from '~/modules/Shared/Infrastructure/SafeVideoElement'
import { ComponentTrophyDto } from '~/modules/Products/Infrastructure/Dtos/ComponentTrophyDto'
import styles from './MintableTrophyCard.module.scss'

interface Props {
  trophyDto: ComponentTrophyDto
}

export const MintableTrophyCard: FC<Props> = ({ trophyDto }) => {
  const { t } = useTranslation('trophies')
  const setIsWalletModalOpen = useSetIsWalletModalOpen()
  const address = useAddress()
  const signer = useSigner()
  const chainId = useChainId()
  const connected = address !== undefined && signer !== undefined && chainId !== undefined
  const [mintStatus, setMintStatus] = useState(trophyDto.mintStatus)
  const [canMint, setCanMint] = useState(false)
  const player: Ref<HTMLVideoElement> = createRef()
  const [playPromise, setPlayPromise] = useState<Promise<void>>(Promise.resolve())

  useEffect(() => {
    setCanMint(mintStatus === 'pending' && connected)
  }, [mintStatus, connected])

  const mint = async () => {
    if (!connected || !trophyDto.id) {
      return
    }

    setMintStatus('minting')
    try {
      await requestMint(
        signer as Signer & TypedDataSigner,
        chainId,
        BigNumber.from(trophyDto.id),
        address
      )
      setMintStatus('minted')
    } catch (exception: unknown) {
      setMintStatus('pending')
      console.log(exception)
    }
  }

  let media
  let connectOrMintButton

  if (trophyDto.animation !== null) {
    media = (
      <video
        className={ styles.mintableTrophyCard__media }
        preload={ 'none' }
        poster={ trophyDto.imageUrl }
        loop={ true }
        autoPlay={ false }
        muted={ true }
        ref={ player }
      >
        <source
          src={ trophyDto.animation.url }
          type={ trophyDto.animation.type }
        />
      </video>
    )
  } else {
    media = (
      <img
        className={ styles.mintableTrophyCard__media }
        src={ trophyDto.imageUrl }
        alt={ trophyDto.description }
      />
    )
  }

  if (connected) {
    if (mintStatus === 'minted') {
      connectOrMintButton = (
        <button
          className={ `
            ${styles.mintableTrophyCard__mintButton}
            ${styles.mintableTrophyCard__mintButton_disabled}
          ` }
          disabled
        >
          { t('mint_trophy_button_done') }
        </button>
      )
    } else {
      connectOrMintButton = (
        <button
          className={ `
            ${styles.mintableTrophyCard__mintButton}
            ${canMint ? '' : styles.mintableTrophyCard__mintButton_disabled}
          ` }
          onClick={ mint }
          disabled={ !canMint }
        >
          {
            mintStatus === 'minting'
              ? <FontAwesomeIcon
                  className={ styles.mintableTrophyCard__loadingIcon }
                  icon={ faRotateRight }
                />
              : <span>{ t('mint_trophy_button') }</span>
          }
        </button>
      )
    }
  } else {
    connectOrMintButton = (
      <button
        className={ styles.mintableTrophyCard__mintButton }
        onClick={ () => setIsWalletModalOpen(true) }
      >
        { t('mint_trophy_connect_wallet_button') }
      </button>
    )
  }

  return (
    <div
      className={ styles.mintableTrophyCard__container }
      onMouseOver={ async () => player.current ? await SafePlayVideo(player.current, setPlayPromise) : '' }
      onMouseLeave={ () => player.current ? SafeStopVideo(player.current, playPromise) : '' }
    >
      <Link
        href={ `/trophies/${trophyDto.id}` }
        className={ styles.mintableTrophyCard__link }
      >
        { t('show_info_button') }
      </Link>

      <div
        className={ styles.mintableTrophyCard__discipline }
      >
        { trophyDto.discipline }
      </div>

      <div className={ styles.mintableTrophyCard__imageWrapper }>
        { media }
      </div>

      <div className={ styles.mintableTrophyCard__data }>
        <div className={ styles.mintableTrophyCard__competition }>
          { trophyDto.content.header }
        </div>
        <div className={ styles.mintableTrophyCard__fullName }>
          { trophyDto.content.title }
        </div>
        <div className={ styles.mintableTrophyCard__extra }>
          { trophyDto.content.subtitle }
        </div>
      </div>

      <div className={ styles.mintableTrophyCard__buttons }>
        { connectOrMintButton }

        <Link
          className={ styles.mintableTrophyCard__memoriesButton }
          href={ `/trophies/${trophyDto.id}/memories` }
        >
          { t('add_memories_button') }
        </Link>
      </div>
    </div>
  )
}
